import { CaretDownIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import type { useDocumentMarkdownEditor } from '@/hooks/useDocumentMarkdownEditor';

/** The same conflict recovery in chat and the immersive reader. No draft is replaced by reloading. */
export function documentEditorStatus(
  editor: ReturnType<typeof useDocumentMarkdownEditor>,
): string | null {
  if (editor.isSaving) return 'Saving…';
  if (editor.isDirty) return 'Unsaved changes';
  return null;
}

/**
 * `showStatus={false}` when the host shows `documentEditorStatus` itself (the
 * article workspace puts it at the end of the formatting toolbar), so only
 * errors and conflict recovery render here and an idle editor has no
 * standing instruction line above the article.
 */
export function DocumentEditorFeedback({
  editor,
  showStatus = true,
}: {
  editor: ReturnType<typeof useDocumentMarkdownEditor>;
  showStatus?: boolean;
}) {
  const hasContent =
    showStatus ||
    Boolean(editor.error) ||
    editor.conflict ||
    editor.comparisonMarkdown !== null ||
    Boolean(editor.storageError);
  if (!hasContent) return null;
  return (
    <div className="flex flex-col gap-2">
      {showStatus ? (
        <p className="text-caption text-muted-foreground">
          {editor.isSaving
            ? 'Saving changes…'
            : editor.isDirty
              ? 'Unsaved changes. Your draft stays in this tab until you save or cancel.'
              : 'Edit article text. Links, headings, and citations are preserved when you save.'}
        </p>
      ) : null}
      {editor.error ? (
        <p role="alert" className="text-caption text-destructive">
          {editor.error}
        </p>
      ) : null}
      {editor.conflict ? (
        <Button
          variant="neutral"
          size="sm"
          className="self-start"
          disabled={editor.isReloading || editor.isSaving}
          onClick={() => void editor.reloadSavedVersion()}
        >
          {editor.isReloading ? 'Loading saved version…' : 'Reload saved version'}
        </Button>
      ) : null}
      {editor.comparisonMarkdown !== null ? (
        <Collapsible defaultOpen>
          <CollapsibleTrigger asChild>
            <Button variant="flat" size="sm" className="group -ml-2">
              <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
              Latest saved version
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="flex flex-col gap-2 pt-2">
            <p className="text-caption text-muted-foreground">
              Your draft is unchanged. Merge any changes from this saved version before saving.
            </p>
            <Textarea
              aria-label="Latest saved document source"
              value={editor.comparisonMarkdown}
              readOnly
              maxRows={12}
              className="font-mono"
            />
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      {editor.storageError ? (
        <p role="alert" className="text-caption text-warning">
          This browser cannot store the draft. Save before closing the tab.
        </p>
      ) : null}
    </div>
  );
}
